import { Stack, TextField, Typography } from '@mui/material';
import z from 'zod';

import { CoordinatesFieldGroup } from '@/components/management/formElements/CoordinatesFieldGroup';
import { useAppForm } from '@/hooks/useAppForm';
import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { I18N_DE } from '@/i18n/de';
import type { Station, UpdateStationDto } from '@/modules/api/backend';
import { zStationType } from '@/modules/api/backend/zod.gen';
import { formScrollIntoViewOptions } from '@/utils/formScrollIntoViewOptions';

interface EditStationFormProps {
  initialValues: Station;
  setRef: (idx: number) => (ref: HTMLDivElement | null) => void;
  tabs: string[];
  onSubmit: (values: UpdateStationDto) => Promise<void>;
  disabled?: boolean;
}

export function EditStationForm({ initialValues, setRef, tabs, onSubmit, disabled }: EditStationFormProps) {
  const form = useAppForm({
    defaultValues: {
      name: initialValues.name,
      type: initialValues.type,
      rmvId: initialValues.rmvId,
      rmvNumber: initialValues.rmvNumber,
      stationComment: initialValues.stationComment,
      numBrFacility: initialValues.numBrFacility,
      numBikeParkingStation: initialValues.numBikeParkingStation,
      numBikeParkingCovered: initialValues.numBikeParkingCovered,
      numBikeReadyToRide: initialValues.numBikeReadyToRide,
      geometry:
        initialValues.geometry && 'coordinates' in initialValues.geometry
          ? {
              longitude: initialValues.geometry.coordinates[0],
              latitude: initialValues.geometry.coordinates[1],
            }
          : { longitude: null as unknown as number, latitude: null as unknown as number },
    },
    ...formScrollIntoViewOptions,
    onSubmit: async ({ value: { geometry, ...rest } }) => {
      if (geometry?.longitude == null || geometry?.latitude == null) return;

      const sanitized = Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, v ?? undefined]));

      await onSubmit({
        ...sanitized,
        geometry: {
          type: 'Point',
          coordinates: [geometry.longitude, geometry.latitude],
        },
      });
      form.reset();
    },
  });

  useNavigationBlocker(form);

  return (
    <form
      id="stationForm"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0 }}>
        <Stack spacing={6}>
          <Stack spacing={2}>
            <Typography ref={setRef(0)} variant="h2">
              {tabs[0]}
            </Typography>

            <form.AppField
              name="name"
              validators={{
                onChange: z.string().nonempty(),
              }}
            >
              {(field) => <field.TextField label="Name" />}
            </form.AppField>

            <form.AppField name="type">
              {(field) => (
                <field.SelectField
                  label="Stationstyp"
                  options={zStationType.options.map((type) => ({ value: type, label: I18N_DE.StationType[type] }))}
                />
              )}
            </form.AppField>

            <form.AppField
              name="rmvId"
              validators={{
                onChange: z.string().nonempty(),
              }}
            >
              {(field) => <field.TextField label="Bahnhofsnummer" />}
            </form.AppField>

            <form.AppField
              name="rmvNumber"
              validators={{
                onChange: z.number(),
              }}
            >
              {(field) => <field.NumberField label="RMV-Nummer" />}
            </form.AppField>

            <CoordinatesFieldGroup disabled={!!disabled} fields="geometry" form={form} />

            <form.Field name="stationComment">
              {(field) => (
                <TextField
                  fullWidth
                  multiline
                  error={!!field.state.meta.errors.length}
                  helperText={field.state.meta.errors[0]}
                  label="Hinweis"
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                  rows={2}
                  value={field.state.value ?? ''}
                />
              )}
            </form.Field>
          </Stack>

          <Stack direction="column" gap={2}>
            <Typography ref={setRef(1)} variant="h2">
              {tabs[1]}
            </Typography>

            <form.Field name="numBrFacility">
              {(field) => (
                <TextField
                  fullWidth
                  label="Anzahl B+R Anlagen"
                  onBlur={field.handleBlur}
                  type="number"
                  value={field.state.value ?? ''}
                  onChange={(event) =>
                    field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                  }
                  slotProps={{
                    htmlInput: { min: 0 },
                  }}
                />
              )}
            </form.Field>

            <form.Field name="numBikeParkingStation">
              {(field) => (
                <TextField
                  fullWidth
                  label="Fahrradabstellplätze stationsweit"
                  onBlur={field.handleBlur}
                  type="number"
                  value={field.state.value ?? ''}
                  onChange={(event) =>
                    field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                  }
                  slotProps={{
                    htmlInput: {
                      min: 0,
                    },
                  }}
                />
              )}
            </form.Field>

            <form.Field name="numBikeParkingCovered">
              {(field) => (
                <TextField
                  fullWidth
                  label="Fahrradstellplätze überdacht"
                  onBlur={field.handleBlur}
                  type="number"
                  value={field.state.value ?? ''}
                  onChange={(event) =>
                    field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                  }
                  slotProps={{
                    htmlInput: {
                      min: 0,
                    },
                  }}
                />
              )}
            </form.Field>

            <form.Field name="numBikeReadyToRide">
              {(field) => (
                <TextField
                  fullWidth
                  label="Fahrbereite Fahrräder"
                  onBlur={field.handleBlur}
                  type="number"
                  value={field.state.value ?? ''}
                  onChange={(event) =>
                    field.handleChange(event.target.value === '' ? null : parseInt(event.target.value, 10))
                  }
                  slotProps={{
                    htmlInput: {
                      min: 0,
                    },
                  }}
                />
              )}
            </form.Field>
          </Stack>
        </Stack>
      </fieldset>
    </form>
  );
}
